import * as React from 'react';
import { clsx } from 'clsx';

export interface ListRowMultiLineProps {
  /**
   * The heading/title text (displayed on line 1, larger and bold)
   */
  title: string;
  /**
   * The description text (displayed on line 2, smaller and regular weight)
   */
  description: string;
  /**
   * Show the right arrow icon - enables hover state and indicates row is clickable
   * @default false
   */
  icon?: boolean;
  /**
   * Click handler - typically used with icon prop for navigation
   */
  onClick?: () => void;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * Additional inline styles
   */
  style?: React.CSSProperties;
}

// Caret/Arrow icon - transforms on hover
const CaretIcon = ({ isHovered }: { isHovered: boolean }) => {
  if (isHovered) {
    // Arrow pointing right on hover
    return (
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M6 4L10 8L6 12"
          stroke="#2f2f2f"
          strokeWidth="1.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }
  // Default caret (chevron right)
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6 4L10 8L6 12"
        stroke="#b3b3b3"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
};

// Arbor Design System list row multiple lines styles
const listRowMultiLineStyles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    borderBottom: '1px solid #f8f8f8',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    boxSizing: 'border-box' as const,
    borderRadius: '8px',
    transition: 'background-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out',
    outline: 'none',
  },
  containerHover: {
    backgroundColor: '#f8f8f8',
  },
  containerFocus: {
    outline: '3px solid #3cad51',
    outlineOffset: '-3px',
  },
  containerClickable: {
    cursor: 'pointer',
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '4px 8px',
    minHeight: '44px',
  },
  textContent: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'flex-start',
    justifyContent: 'center',
    flex: 1,
    gap: '0px',
  },
  title: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#2f2f2f',
    lineHeight: '1.5',
    margin: 0,
  },
  description: {
    fontSize: '13px',
    fontWeight: '400',
    color: '#2f2f2f',
    lineHeight: '1.5',
    margin: 0,
  },
  iconWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    flexShrink: 0,
    marginLeft: '8px',
  },
};

/**
 * ListRowMultiLine component - Arbor Design System
 *
 * A list row component with content displayed on two vertical lines.
 * Cannot be mixed with single-line ListRow in the same section.
 * Hover state is only enabled when icon is present (indicating clickable row).
 *
 * @example
 * ```tsx
 * // Basic multi-line row (not clickable)
 * <ListRowMultiLine
 *   title="Section Title"
 *   description="A brief description of this section"
 * />
 *
 * // Clickable multi-line row with icon
 * <ListRowMultiLine
 *   title="View Details"
 *   description="Click to see more information"
 *   icon
 *   onClick={() => navigate('/details')}
 * />
 * ```
 */
export const ListRowMultiLine = React.forwardRef<HTMLDivElement, ListRowMultiLineProps>(
  ({ title, description, icon = false, onClick, className, style, ...props }, ref) => {
    const [isHovered, setIsHovered] = React.useState(false);
    const [isFocused, setIsFocused] = React.useState(false);

    // Only enable hover state when icon is present
    const showHoverState = icon && isHovered;
    const isClickable = icon && onClick;

    const containerStyle: React.CSSProperties = {
      ...listRowMultiLineStyles.container,
      ...(showHoverState && listRowMultiLineStyles.containerHover),
      ...(isFocused && listRowMultiLineStyles.containerFocus),
      ...(isClickable && listRowMultiLineStyles.containerClickable),
      ...style,
    };

    const handleClick = () => {
      if (isClickable && onClick) {
        onClick();
      }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
      if (isClickable && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault();
        onClick?.();
      }
    };

    return (
      <div
        ref={ref}
        className={clsx('arbor-list-row-multi-line', className)}
        style={containerStyle}
        onClick={handleClick}
        onMouseEnter={() => icon && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
        tabIndex={isClickable ? 0 : undefined}
        role={isClickable ? 'button' : undefined}
        {...props}
      >
        <div style={listRowMultiLineStyles.content}>
          <div style={listRowMultiLineStyles.textContent}>
            <span style={listRowMultiLineStyles.title}>{title}</span>
            <span style={listRowMultiLineStyles.description}>{description}</span>
          </div>
          {icon && (
            <div style={listRowMultiLineStyles.iconWrapper}>
              <CaretIcon isHovered={isHovered} />
            </div>
          )}
        </div>
      </div>
    );
  }
);

ListRowMultiLine.displayName = 'ListRowMultiLine';
